import type { IntlShape } from 'react-intl';
import type { PeopleRow } from '../outreachTypes';
import { pitchKindName } from './pitchKindCopy';
import { peopleMessages as messages } from './peopleMessages';

/** The words of one People row (design outreach-v2 g1). */
export interface PeopleRowCopy {
  title: string;
  sub: string;
  /** The text of the result chip and its tone. */
  result: { text: string; tone: 'pos' | 'vio' | 'muted' };
}

function clock(intl: IntlShape, iso: string): string {
  return intl.formatTime(new Date(iso), { hour: '2-digit', minute: '2-digit' });
}

function resultOf(intl: IntlShape, row: PeopleRow): PeopleRowCopy['result'] {
  switch (row.result) {
    case 'added':
      return { text: intl.formatMessage(messages.resultAdded), tone: 'pos' };
    case 'replied':
      return { text: intl.formatMessage(messages.resultReplied), tone: 'vio' };
    case 'listened':
      return { text: intl.formatMessage(messages.resultListened), tone: 'pos' };
    case 'opened':
      return { text: intl.formatMessage(messages.resultOpened), tone: 'muted' };
    case 'followUpOn':
      return {
        text:
          row.followUpAt === null
            ? intl.formatMessage(messages.resultFollowUpPlain)
            : intl.formatMessage(messages.resultFollowUp, {
                date: intl.formatDate(new Date(row.followUpAt), { month: 'short', day: 'numeric' }),
              }),
        tone: 'muted',
      };
    case 'scheduled':
      return { text: intl.formatMessage(messages.resultScheduled), tone: 'vio' };
    case 'sent':
    default:
      return { text: intl.formatMessage(messages.resultSent, { time: clock(intl, row.at) }), tone: 'muted' };
  }
}

/**
 * The title, the line under it and the result chip of a row. A planned (`scheduled`) row names the
 * person too (owner decision 2026-10-27); its state cell shows where the pitch is.
 */
export function peopleRowCopy(intl: IntlShape, row: PeopleRow): PeopleRowCopy {
  const result = resultOf(intl, row);
  const parts = [row.kind === null ? intl.formatMessage(messages.ownContact) : pitchKindName(intl, row.kind)];
  if (row.followers !== null) {
    parts.push(intl.formatMessage(messages.followers, { count: row.followers }));
  }
  if (row.city !== null) {
    parts.push(row.city);
  }
  const title = row.name ?? (row.kind === null ? '' : pitchKindName(intl, row.kind));
  return { title, sub: parts.join(' · '), result };
}

/** "Tue 10:20": the send time of a planned row, in the viewer's zone; null while it has none. */
export function plannedWhen(intl: IntlShape, row: PeopleRow): string | null {
  if (row.result !== 'scheduled' || row.plannedState === 'writing' || row.plannedState === 'review') {
    return null;
  }
  return intl.formatDate(new Date(row.at), { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}
